"use client";

import { FC, ReactNode, useEffect } from "react";
import { useInView } from "react-intersection-observer";
import Spinner from "@/components/ui/Spinner";
import React from "react";
import _ from "lodash";
import { useTranslations } from "next-intl";
import { cn } from "@apg.gg/core/lib/cn";

interface InfiniteScrollProps<T> {
  data: T,
  fetchFunction: () => void,
  renderItem: (item: T) => JSX.Element;
  status: string;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  error: Error;
  endMessage?: ReactNode;
  emptyMessage?: ReactNode;
  className?: string;
}

const InfiniteScroll: FC<InfiniteScrollProps<any>> = ({ 
  data, 
  fetchFunction, 
  renderItem, 
  status, 
  isFetchingNextPage, 
  hasNextPage,
  error, 
  endMessage,
  emptyMessage,
  className = ''
}) => {
  const t = useTranslations('Common');
  const { ref, inView } = useInView();
  
  useEffect(() => {
    if (inView) {
      fetchFunction()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView])

  return (
    <>
      {status === 'pending' ? (
        <div className={cn(
          "flex items-center justify-center w-full col-span-1 md:col-span-2 lg:col-span-3",
          className
        )}>
          <Spinner />
        </div>
      ) : status === 'error' ? (
        <span>Error: {error.message}</span>
      ) : (
        <>
          {data.pages.map((page: any, pageIndex: number) => (
            <React.Fragment key={`page-${page.nextId}}-${pageIndex}`}>
              {page.items.map((item: any, index: number) => (
                <React.Fragment key={`card-profile-${item.id}-${index}`}>
                  {renderItem(item)}
                </React.Fragment>
              ))}
            </React.Fragment>
          ))}
          {data.pages.every((page: any) => page.total === 0) ? (
            <div className={cn(
              "flex text-title-lg items-center justify-center w-full flex-1 col-span-1 md:col-span-2 lg:col-span-3",
              className
            )}>
              {emptyMessage || `${t('infinite-no-results')} 😅` } 
            </div>
          ) : (
            <div ref={ref} className={cn(
              "flex items-center justify-center w-full col-span-1 md:col-span-2 lg:col-span-3",
              className
            )}>
              {isFetchingNextPage
                ? <Spinner />
                : hasNextPage
                ? 'Load Newer'
                : endMessage || 
                  <div className="text-white mt-8 text-title-lg">
                    {t('infinite-you-reached-the-end')} 🎉😅
                  </div>
              }
            </div>
          )}
        </>
      )}
    </>
  );
}

export default InfiniteScroll